import {
  Subject,
  debounceTime,
  distinctUntilChanged,
  pairwise,
  startWith,
  takeUntil,
} from 'rxjs';

import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  inject,
} from '@angular/core';
import { FormControl } from '@angular/forms';

import { COUNTRY_LIST } from '../../../constants/address';
import { AutocompleteOption, physicalAddressForm } from '../../interfaces';
import { GeoService, UiHelpersService } from '../../services';

@Component({
  selector: 'phoenix-physical-address',
  templateUrl: './physical-address.component.html',
  standalone: false,
})
export class PhysicalAddressComponent implements OnInit, OnChanges, OnDestroy {
  @Input() address = physicalAddressForm();
  @Input() showCoordinates = false;
  @Output() contentChange = new EventEmitter<any>();

  countryList = COUNTRY_LIST;
  latLon: any;
  featuresResponse!: any[];
  geoResponse!: any;

  loadingRequest = false;

  geoService = inject(GeoService);
  destroy$ = new Subject<void>();
  constructor(readonly uiHelpers: UiHelpersService) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['address']) {
      this.address.controls.coords.controls.lat.disable();
      this.address.controls.coords.controls.lon.disable();

      this.address.statusChanges
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => {
          if (this.address.controls.coords.controls.lat.enabled) {
            this.address.controls.coords.controls.lat.disable();
            this.address.controls.coords.controls.lon.disable();
          }
        });
    }
  }

  ngOnInit() {
    if (!this.address) {
      this.address = physicalAddressForm();
    } else {
      this.patchAddressToForm();
    }
    this.address.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.onContentChange();
    });

    this.address.controls.raw.valueChanges
      .pipe(
        takeUntil(this.destroy$),
        distinctUntilChanged(),
        debounceTime(1000),
        startWith(this.address.controls.raw.value),
        pairwise()
      )
      .subscribe(([prev, curr]) => {
        if (curr && this.address.enabled && curr !== prev) {
          this.parseRaw(curr);
        }
      });
  }

  patchAddressToForm() {
    if (this.address) {
      this.address.patchValue(this.address.value);
    }
  }

  getControl(name: string) {
    return this.address.get(name) as FormControl;
  }

  customFilter = (value: AutocompleteOption | string): string => {
    if (typeof value === 'string') {
      return value;
    }
    return `${value['display']}`;
  };

  onContentChange() {
    this.address.markAsDirty();
    this.contentChange.emit(this.address.value);
  }

  onCountryChange(event: any) {
    this.address.get('country')?.setValue(event);
    this.address.get('state')?.setValue('');

    this.onContentChange();
  }

  findCountryStates() {
    const selectedCountry = this.address.get('country')?.value;
    const country = this.countryList.find(
      e => e.display === selectedCountry || e.id === selectedCountry
    );

    if (country?.states) {
      return country.states;
    } else {
      return false;
    }
  }

  get checkAddress() {
    return (
      this.address?.controls?.street?.value?.trim() !== '' ||
      this.address?.controls?.city?.value?.trim() !== '' ||
      this.address?.controls?.state?.value?.trim() !== '' ||
      this.address?.controls?.postal_code?.value?.trim() !== '' ||
      this.address?.controls?.country?.value?.trim() !== ''
    );
  }

  parseRaw(address: string) {
    if (address && address.trim() !== '') {
      this.geoService
        .parsePelias(address, 1)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: response => {
            this.parsePelias(response);
          },
          error: () => {
            this.uiHelpers.errorSnackbar(`Error parsing Address`);
          },
        });
    } else {
      this.address.controls.coords.patchValue({ lat: null, lon: null });
    }
  }

  parsePelias(response: any): void {
    let parsed;
    try {
      this.checkCoordinates(response);
      this.parseFeatures(response.features);

      this.featuresResponse = response.features;
      this.geoResponse = response.geocoding;
      // Prioritize the best response: geocoding + features, then features, then geocoding
      if (this.hasValidGeocodingAndFeatures(response)) {
        parsed = this.parseAddress();
      } else if (response?.features.length > 0) {
        parsed = this.parseFeatures(response.features);
      } else if (this.hasValidGeocoding(response)) {
        parsed = this.parseGeocoding(response.geocoding);
      } else {
        throw new Error('Invalid response');
      }
    } catch (error) {
      this.uiHelpers.informationSnackbar(
        `Address parsing with no results, please check manually`
      );
      return;
    }

    this.fillAddressFields(parsed);
    this.uiHelpers.informationSnackbar(
      `Address parsing has been completed. Please review the information.`,
      10000
    );
    this.onContentChange();
  }

  checkCoordinates(response: any) {
    this.latLon = null;
    const geoData = response?.features[0]?.geometry?.coordinates;
    const lat = geoData[1];
    const lon = geoData[0];
    if (lat && lon) {
      this.latLon = { lat, lon };
    } else {
      this.address.get('coords')?.setValue({ lat: null, lon: null });
    }
  }

  isEmptyObject(obj: {}): boolean {
    return Object.keys(obj).length === 0;
  }

  getManualCoordinates() {
    const addressComponents = this.buildAddressComponents();

    this.loadingRequest = true;
    this.geoService
      .parsePelias(addressComponents, 1)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: response => {
          this.handleGeoResponse(response);
        },
        error: () => {
          this.uiHelpers.errorSnackbar(`Error getting coordinates`);
          this.loadingRequest = false;
        },
      });
  }

  private hasValidGeocodingAndFeatures(response: any): boolean {
    return (
      response.features?.length > 0 &&
      response.geocoding &&
      !this.uiHelpers.isEmptyObject(response.geocoding)
    );
  }

  private hasValidGeocoding(response: any): boolean {
    return (
      response.geocoding && !this.uiHelpers.isEmptyObject(response.geocoding)
    );
  }

  private parseAddress(): any {
    const { properties: parseFeaturesResponse } = this.featuresResponse[0];
    let parseGeoResponse = this.firstLetterToUpperCase(
      this.geoResponse.query.parsed_text,
      ['street', 'locality', 'city']
    );

    const {
      street,
      housenumber,
      city,
      locality,
      region,
      postalcode,
      postcode,
      country,
      country_a,
    } = parseGeoResponse;
    const {
      region_a,
      postalcode: featuresPostalCode,
      country_a: featuresCountryA,
      country: featuresCountry,
      country_code: featuresCountryCode,
    } = parseFeaturesResponse;

    const formattedHouseNumber = `${housenumber ? housenumber + ' ' : ''}${street}`;

    const fullStreet = street ? formattedHouseNumber : '';
    const resolvedCity = city || locality;
    const resolvedRegion =
      region_a || (region ? region.toUpperCase() : undefined);
    const resolvedPostalCode = postalcode || postcode || featuresPostalCode;
    const resolvedCountry =
      featuresCountryCode ||
      this.findCountryCode(
        country ||
          country_a ||
          featuresCountryA ||
          featuresCountry ||
          featuresCountryCode
      );

    return {
      street: fullStreet,
      city: resolvedCity,
      state: resolvedRegion,
      postalCode: resolvedPostalCode,
      country: resolvedCountry,
    };
  }

  private parseFeatures(features: any[]): any {
    const { properties } = features[0];
    let {
      layer,
      neighbourhood,
      locality,
      localadmin,
      region_a: state,
      postalcode: postalCode,
      country,
      country_a,
      name,
    } = properties;

    let street = '';
    let city = '';

    switch (layer) {
      case 'address':
      case 'street':
        street = name;
        city = this.getFirstNonBlank(locality, neighbourhood, localadmin);
        break;
      case 'postalcode':
      case 'microhood':
      case 'neighbourhood':
      case 'macrohood':
      case 'borough':
      case 'localadmin':
      case 'locality':
        city = this.getFirstNonBlank(locality, neighbourhood, localadmin);
    }

    return {
      street,
      city,
      state,
      postalCode,
      country: this.findCountryCode(country_a || country) || '',
    };
  }

  parseGeocoding(geocoding: any): any {
    let { parsed_text: parsedAddress } = geocoding.query;
    parsedAddress = this.firstLetterToUpperCase(parsedAddress, [
      'street',
      'locality',
    ]);

    let street = '';

    if (parsedAddress.street) {
      street =
        (parsedAddress.housenumber ? parsedAddress.housenumber + ' ' : '') +
        parsedAddress.street;
    }

    return {
      street,
      city: parsedAddress.locality,
      state: parsedAddress.region
        ? parsedAddress.region.toUpperCase()
        : undefined,
      postalCode: parsedAddress.postalcode,
      country: this.findCountryCode(
        parsedAddress.country || parsedAddress.country_a
      ),
    };
  }

  private fillAddressFields(address: any) {
    this.address.controls.country.setValue(address.country);

    this.address.controls.street.setValue(
      String(
        (address.housenumber ? address.housenumber + ' ' : '') + address.street
      ).trim()
    );

    this.address.controls.city.setValue(String(address?.city ?? ''));
    this.address.controls.postal_code.setValue(
      String(address?.postalCode ?? '')
    );
    this.address.controls.state.setValue(String(address?.state ?? ''));
    this.address.controls.coords.setValue(this?.latLon ?? null);
  }

  private findCountryCode(country: string) {
    const countryISO =
      this.countryList.find(obj => obj.code3 === country) ||
      this.countryList.find(obj => obj.display === country);
    if (countryISO) {
      return countryISO.id;
    } else {
      return '';
    }
  }

  private firstLetterToUpperCase(obj: any, keys: any) {
    for (const key of keys) {
      if (obj[key]) {
        obj[key] = obj[key]
          .split(' ')
          .map((word: any) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ');
      }
    }
    return obj;
  }

  private isNotBlank(field: any) {
    return field && field !== '';
  }

  private getFirstNonBlank(...values: any[]) {
    return values.find(v => this.isNotBlank(v)) || '';
  }

  private handleGeoResponse(response: any): void {
    try {
      this.checkCoordinates(response);

      this.address.controls.coords.setValue(this.latLon ?? null);
      this.uiHelpers.informationSnackbar(`Coordinates found`, 10000);
      this.loadingRequest = false;
    } catch (error) {
      this.uiHelpers.errorSnackbar(`Address parsing with no coordinates found`);
      this.loadingRequest = false;
    }
  }

  private buildAddressComponents(): string {
    const { country, city, street, state, postal_code } = this.address.controls;
    const countryValue = country.value ?? '';
    const cityValue = city.value ?? '';
    const streetValue = street.value ?? '';
    const stateValue = state.value ?? '';
    const postalCodeValue = postal_code.value ?? '';

    return [streetValue, cityValue, stateValue, postalCodeValue, countryValue]
      .map(String)
      .filter(Boolean)
      .join(', ');
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
